import React, { useEffect, useMemo, useState } from 'react';
import { TextField, SelectField, ToggleField, FormSection } from '../../../components/orizon-ui/FormField';
import { useToast } from '../../../components/orizon-ui/Toast';
import {
  gruCrea, gruAggiorna, gruOpzioni, gruCampiReparto, mapGruError,
} from '../../../lib/services/gruService';
import CampiDinamici, { bozzaDaAttributi, attributiDaBozza } from '../../../components/gru/CampiDinamici';
import FormModal, { useFormSalva } from '../../../components/gru/FormModal';
import SezioneConfig, { colonnaAttivo } from './SezioneConfig';
import { vuoto } from '../utils';

/** Opzioni di un dropdown da un elenco di righe: mostra gli attivi + quello già selezionato (anche se disattivato). */
const opzioniDropdown = (righe, valoreCorrente, etichetta) => righe
  .filter((r) => r.attivo || r.id === valoreCorrente)
  .map((r) => ({ value: r.id, label: `${etichetta(r)}${r.attivo ? '' : ' (disattivato)'}` }));

// ─── Form componente ─────────────────────────────────────────────────────────

const FormComponente = ({ record, onClose, onSaved }) => {
  const modifica = Boolean(record);
  const toast = useToast();

  const [elencoGru, setElencoGru] = useState([]);
  const [elencoReparti, setElencoReparti] = useState([]);
  const [caricamento, setCaricamento] = useState(true);

  const [gruId, setGruId] = useState(record?.gru_id ?? '');
  const [repartoId, setRepartoId] = useState(record?.reparto_id ?? '');
  const [etichetta, setEtichetta] = useState(record?.etichetta ?? '');
  const [attivo, setAttivo] = useState(record?.attivo ?? true);

  const [campi, setCampi] = useState([]);
  const [caricamentoCampi, setCaricamentoCampi] = useState(false);
  const [bozza, setBozza] = useState({});
  // Snapshot iniziale per rilevare le modifiche non salvate. Nuovo componente: subito.
  // Modifica: dopo il caricamento dei campi (la bozza dipende da essi).
  const [base, setBase] = useState(
    record ? null : JSON.stringify({ gruId: '', repartoId: '', etichetta: '', attivo: true, bozza: {} }),
  );

  const { salvando, errori, impostaErrori, pulisci, salva } = useFormSalva();

  // elenchi per i dropdown (da tabelle, non da codice)
  useEffect(() => {
    let annullato = false;
    Promise.all([gruOpzioni('gru'), gruOpzioni('reparti')])
      .then(([g, r]) => {
        if (annullato) return;
        setElencoGru(g);
        setElencoReparti(r);
        setCaricamento(false);
      })
      .catch((e) => {
        if (annullato) return;
        toast.error(mapGruError(e).message);
        setCaricamento(false);
      });
    return () => { annullato = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // campi dinamici del reparto scelto
  useEffect(() => {
    if (!repartoId) {
      setCampi([]);
      return undefined;
    }
    let annullato = false;
    setCaricamentoCampi(true);
    gruCampiReparto(repartoId)
      .then((righe) => {
        if (annullato) return;
        setCampi(righe);
        const iniziale = repartoId === record?.reparto_id ? bozzaDaAttributi(righe, record?.attributi) : {};
        setBozza(iniziale);
        setBase((prec) => prec ?? JSON.stringify({ gruId, repartoId, etichetta, attivo, bozza: iniziale }));
        setCaricamentoCampi(false);
      })
      .catch((e) => {
        if (annullato) return;
        toast.error(mapGruError(e).message);
        setCaricamentoCampi(false);
      });
    return () => { annullato = true; };
    // gli altri valori servono solo per lo snapshot iniziale
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repartoId]);

  const sporco = base !== null
    && JSON.stringify({ gruId, repartoId, etichetta, attivo, bozza }) !== base;

  const opzioniGru = useMemo(
    () => opzioniDropdown(elencoGru, record?.gru_id, (g) => `${g.codice} — ${g.nome}`),
    [elencoGru, record],
  );
  const opzioniReparti = useMemo(
    () => opzioniDropdown(elencoReparti, record?.reparto_id, (r) => r.label),
    [elencoReparti, record],
  );

  const invia = async () => {
    const nuovi = {};
    if (vuoto(gruId)) nuovi.gru_id = 'Obbligatorio';
    if (vuoto(repartoId)) nuovi.reparto_id = 'Obbligatorio';

    const { attributi, errori: erroriAttributi } = attributiDaBozza(campi, bozza, record?.attributi);
    Object.assign(nuovi, erroriAttributi);

    impostaErrori(nuovi);
    if (Object.keys(nuovi).length) return;

    const payload = {
      gru_id: gruId,
      reparto_id: repartoId,
      etichetta: vuoto(etichetta) ? null : etichetta.trim(),
      attributi,
      attivo,
    };

    await salva(
      () => (modifica ? gruAggiorna('componenti', record.id, payload) : gruCrea('componenti', payload)),
      modifica ? 'Componente aggiornato.' : 'Componente creato.',
      onSaved,
    );
  };

  return (
    <FormModal
      titolo={modifica ? 'Modifica componente' : 'Nuovo componente'}
      etichetta="Configurazione · Componenti"
      larghezza="xl"
      sporco={sporco}
      salvando={salvando}
      onClose={onClose}
      onSubmit={invia}
    >
      <FormSection titolo="Gru e reparto" icona="Construction">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <SelectField
            label="Gru" name="gru_id" value={gruId}
            onChange={(v) => { setGruId(v); pulisci('gru_id'); }}
            opzioni={opzioniGru} obbligatorio errore={errori.gru_id}
            disabilitato={caricamento}
          />
          <SelectField
            label="Reparto" name="reparto_id" value={repartoId}
            onChange={(v) => { setRepartoId(v); pulisci('reparto_id'); }}
            opzioni={opzioniReparti} obbligatorio errore={errori.reparto_id}
            disabilitato={caricamento || modifica}
            suggerimento={modifica ? 'Il reparto non si cambia dopo la creazione.' : undefined}
          />
          <TextField
            label="Etichetta" name="etichetta" value={etichetta}
            onChange={(v) => { setEtichetta(v); pulisci('etichetta'); }}
            placeholder="es. Motore sollevamento principale" errore={errori.etichetta}
          />
        </div>
      </FormSection>

      <FormSection titolo="Dati tecnici del reparto" icona="Cpu">
        {!repartoId ? (
          <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/40">
            Seleziona un reparto per compilare i dati tecnici.
          </p>
        ) : caricamentoCampi ? (
          <div className="flex items-center gap-3 text-muted-foreground/50">
            <div className="w-5 h-5 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
            <span className="text-[10px] font-black uppercase tracking-widest">Caricamento campi...</span>
          </div>
        ) : (
          <CampiDinamici
            campi={campi}
            bozza={bozza}
            onChange={(b) => {
              Object.keys(b)
                .filter((k) => b[k] !== bozza[k])
                .forEach((k) => pulisci(`attributi.${k}`));
              setBozza(b);
            }}
            errori={errori}
          />
        )}
      </FormSection>

      <ToggleField label="Attivo" sublabel={attivo ? 'Selezionabile negli interventi' : 'Non selezionabile'} checked={attivo} onChange={setAttivo} />
    </FormModal>
  );
};

// ─── Lista componenti ────────────────────────────────────────────────────────

const COLONNE = [
  { key: 'gru_codice', label: 'Gru', width: 130, render: (r) => <span className="font-mono text-[12px] font-bold text-foreground/80">{r.gru_codice}</span> },
  { key: 'reparto_label', label: 'Reparto', width: 140, render: (r) => <span className="text-[13px] font-black uppercase tracking-tight text-foreground/80">{r.reparto_label}</span> },
  { key: 'posizione', label: 'Pos.', width: 80, align: 'center', render: (r) => <span className="text-[12px] font-black text-primary/70">{r.posizione || '—'}</span> },
  { key: 'etichetta', label: 'Etichetta', width: 240, render: (r) => <span className="text-[12px] font-medium text-muted-foreground">{r.etichetta || '---'}</span> },
  {
    key: 'attributi', label: 'Dati tecnici', width: 130, align: 'center', noSort: true,
    render: (r) => {
      const n = Object.keys(r.attributi || {}).length;
      return <span className="text-[11px] font-black uppercase tracking-widest text-muted-foreground/60">{n} {n === 1 ? 'valore' : 'valori'}</span>;
    },
  },
  { key: 'n_articoli', label: 'Articoli', width: 100, align: 'center', render: (r) => <span className="text-[13px] font-black tabular-nums text-foreground/80">{r.n_articoli}</span> },
  colonnaAttivo,
];

const TabComponenti = () => {
  const toast = useToast();
  const [elencoGru, setElencoGru] = useState([]);
  const [elencoReparti, setElencoReparti] = useState([]);
  const [filtroGru, setFiltroGru] = useState('');
  const [filtroReparto, setFiltroReparto] = useState('');

  useEffect(() => {
    let annullato = false;
    Promise.all([gruOpzioni('gru'), gruOpzioni('reparti')])
      .then(([g, r]) => {
        if (annullato) return;
        setElencoGru(g);
        setElencoReparti(r);
      })
      .catch((e) => {
        if (!annullato) toast.error(mapGruError(e).message);
      });
    return () => { annullato = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const classeFiltro = 'bg-muted/40 border border-border/40 rounded-2xl px-3 py-2.5 text-[11px] font-black uppercase tracking-widest text-muted-foreground outline-none focus:border-primary/60 focus:ring-2 focus:ring-primary/10 transition-all cursor-pointer shadow-sm';

  const filtri = {};
  if (filtroGru) filtri.gru_id = filtroGru;
  if (filtroReparto) filtri.reparto_id = filtroReparto;

  return (
    <SezioneConfig
      entita="componenti"
      titolo="Componenti"
      descrizione="Istanze di un reparto montate su ciascuna gru"
      nuovoLabel="Nuovo componente"
      colonne={COLONNE}
      ordineIniziale={{ key: 'gru_codice', direction: 'asc' }}
      filtri={filtri}
      FormComponent={FormComponente}
      labelRiga={(r) => `${r.gru_codice} · ${r.reparto_label}${r.posizione ? ` ${r.posizione}` : ''}${r.etichetta ? ` — ${r.etichetta}` : ''}`}
      messaggioVuoto="Nessun componente configurato"
      toolbarExtra={(
        <>
          <select value={filtroGru} onChange={(e) => setFiltroGru(e.target.value)} className={classeFiltro} aria-label="Filtra per gru">
            <option value="">Tutte le gru</option>
            {elencoGru.map((g) => <option key={g.id} value={g.id}>{g.codice}</option>)}
          </select>
          <select value={filtroReparto} onChange={(e) => setFiltroReparto(e.target.value)} className={classeFiltro} aria-label="Filtra per reparto">
            <option value="">Tutti i reparti</option>
            {elencoReparti.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select>
        </>
      )}
    />
  );
};

export default TabComponenti;
